// MessageInput.js
import React, { useRef, useState } from 'react';
import { TextField, IconButton, InputAdornment, Box, Stack, Chip, Typography } from '@mui/material';

import SendIcon from '@mui/icons-material/Send';
import AttachFileIcon from '@mui/icons-material/AttachFile';

// The Logic Assistant refuses any attachment over this size (backend
// MAX_FILE_SIZE), so an oversized file is refused here, at selection, where the
// prompt and the other files are still intact — a refusal after Send would
// have cost the whole draft.
const MAX_ATTACHMENT_BYTES = 20 * 1024 * 1024;
const MAX_ATTACHMENT_LABEL = '20 MB';
// The backend's MAX_FILES_PER_MESSAGE; refused at selection for the same reason.
const MAX_ATTACHMENTS = 10;

function MessageInput({
  onSendMessage,
  isDisabled,
  allowAttachments = false,
  // Controlled mode: pass both to own the draft text outside this component
  // (e.g. so it survives the component unmounting). Omit both for the
  // original self-contained behaviour.
  value,
  onChange,
  files = [],
  onFilesChange,
  clearOnSend = true,
}) {
  const [internalMessage, setInternalMessage] = useState('');
  const controlled = typeof onChange === 'function';
  const message = controlled ? (value || '') : internalMessage;
  const setMessage = controlled ? onChange : setInternalMessage;

  const [internalFiles, setInternalFiles] = useState([]);
  const selectedFiles = onFilesChange ? files : internalFiles;
  const setSelectedFiles = onFilesChange || setInternalFiles;
  const [fileError, setFileError] = useState('');
  const fileInputRef = useRef(null);

  const handleFilesChosen = (chosen) => {
    const tooLarge = chosen.filter((file) => file.size > MAX_ATTACHMENT_BYTES);
    const withinSize = chosen.filter((file) => file.size <= MAX_ATTACHMENT_BYTES);
    const accepted = withinSize.slice(0, MAX_ATTACHMENTS);
    const overCount = withinSize.slice(MAX_ATTACHMENTS);
    const problems = [];
    if (tooLarge.length > 0) {
      problems.push(`Not attached — larger than ${MAX_ATTACHMENT_LABEL}: ${tooLarge.map((file) => file.name).join(', ')}.`);
    }
    if (overCount.length > 0) {
      problems.push(`Not attached — over the ${MAX_ATTACHMENTS}-file limit per message: ${overCount.map((file) => file.name).join(', ')}.`);
    }
    setFileError(problems.join(' '));
    setSelectedFiles(accepted);
  };

  const handleSend = () => {
    if (isDisabled) return;
    if (message.trim() !== '' || selectedFiles.length > 0) {
      onSendMessage(message.trim(), selectedFiles);
      if (clearOnSend) {
        setMessage('');
        setSelectedFiles([]);
      }
      setFileError('');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSend();
    }
  };

  return (
    <Box display="flex" flexDirection="column" gap={1}>
      {allowAttachments && (
        <>
          <input
            type="file"
            ref={fileInputRef}
            style={{ display: 'none' }}
            multiple
            accept=".xlsx,.xls,.csv,.json,.docx,.png,.jpg,.jpeg,.gif,.webp,.pdf"
            onChange={(e) => {
              handleFilesChosen(Array.from(e.target.files || []));
              e.target.value = '';
            }}
          />
          {fileError && (
            <Typography variant="caption" color="error">{fileError}</Typography>
          )}
          {selectedFiles.length > 0 && (
            <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
              {selectedFiles.map((file, index) => (
                <Chip
                  key={`${file.name}-${index}`}
                  label={file.name}
                  disabled={!!isDisabled}
                  onDelete={() => setSelectedFiles(selectedFiles.filter((_, fileIndex) => fileIndex !== index))}
                  size="small"
                />
              ))}
            </Stack>
          )}
        </>
      )}
      <TextField
        variant="outlined"
        fullWidth
        multiline
        maxRows={4}
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        onKeyPress={handleKeyPress}
        placeholder="Type your message..."
        disabled={!!isDisabled}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              {allowAttachments && (
                <IconButton onClick={() => fileInputRef.current?.click()} disabled={!!isDisabled}>
                  <AttachFileIcon />
                </IconButton>
              )}
              <IconButton onClick={handleSend} disabled={!!isDisabled || (message.trim() === '' && selectedFiles.length === 0)}>
                <SendIcon />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
    </Box>
  );
}

export default MessageInput;
